import type { MessagePart, TextPart } from '@elara/domain/models/schemas/message-schemas';

import type { ToolCall } from '#models/ToolCallModel.js';

import { isTextPart, isToolRequestPart } from '@elara/domain/models/schemas/message-schemas';

/**
 * Merges adjacent TextParts into single TextParts for proper markdown rendering (ELARA-754).
 *
 * When messages are streamed from the server, each chunk becomes a separate TextPart in Firestore.
 * Without merging, each TextPart renders as a separate paragraph with line breaks appearing
 * mid-sentence.
 *
 * This is pure message-content logic (no DOM, no React), so it lives in `@elara/client-core` and is
 * consumed by both the web chat UI (`#ui/chat/utils`) and `MessageUiState`.
 *
 * @param parts - The content array from a message
 * @returns A new array with adjacent TextParts merged
 */
export function mergeAdjacentTextParts(parts: MessagePart[]): MessagePart[] {
  if (!Array.isArray(parts) || parts.length === 0) return [];
  const result: MessagePart[] = [];
  for (const part of parts) {
    const lastPart = result.at(-1);
    if (lastPart && isTextPart(lastPart) && isTextPart(part)) {
      // Merge adjacent text parts - create new object to avoid mutation
      const merged: TextPart = { text: lastPart.text + part.text };
      result[result.length - 1] = merged;
    } else {
      result.push(part);
    }
  }
  return result;
}

/**
 * The most recently referenced tool call that's still running, by content order (last `toolRequest`
 * part whose ref points at an active call). This is what a "model is thinking" indicator should show
 * instead of a generic placeholder — a slow tool call (flight search, hotel search) reads better than
 * an unrelated rotating phrase. Ignores `updateWorkingMemory` (an internal housekeeping call, never
 * user-facing). Shared by web's `MessageUiState.latestInProgressToolCall` and mobile's thinking
 * indicator so both platforms agree on which tool call "wins."
 */
export function findLatestInProgressToolCall(content: MessagePart[], toolCalls: ToolCall[]): ToolCall | undefined {
  const activeIds = new Set(
    toolCalls.filter((tc) => tc.isActive && tc.toolName !== 'updateWorkingMemory').map((tc) => tc.id),
  );
  if (activeIds.size === 0) return undefined;
  const lastPart = content
    .filter(isToolRequestPart)
    .findLast((part) => part.toolRequest.ref != null && activeIds.has(part.toolRequest.ref));
  const ref = lastPart?.toolRequest.ref;
  return ref != null ? toolCalls.find((tc) => tc.id === ref) : undefined;
}
